import { API_ORIGIN, LOGIN_PATH, LOGIN_WITH_CREDENTIALS } from '@/config/env';

import { ApiError, request } from './httpClient';

export interface Credentials {
  identifier: string;
  password: string;
}

export interface AuthUser {
  id?: string;
  email?: string;
  name?: string;
}

export interface Session {
  token: string | null;
  user: AuthUser | null;
}

/**
 * Login response shapes differ between backends (`token`, `access`, `key`,
 * nested `data`). Normalising here keeps that churn out of the UI and means a
 * backend change is a one-function edit.
 */
function toSession(payload: unknown): Session {
  if (!payload || typeof payload !== 'object') return { token: null, user: null };

  const root = payload as Record<string, unknown>;
  const data = (root.data && typeof root.data === 'object' ? (root.data as Record<string, unknown>) : root) ?? root;

  const token =
    [data.token, data.access, data.access_token, data.accessToken, data.key].find(
      (value): value is string => typeof value === 'string' && value.length > 0,
    ) ?? null;

  const rawUser = data.user && typeof data.user === 'object' ? (data.user as Record<string, unknown>) : null;
  const user: AuthUser | null = rawUser
    ? {
        id: typeof rawUser.id === 'string' || typeof rawUser.id === 'number' ? String(rawUser.id) : undefined,
        email: typeof rawUser.email === 'string' ? rawUser.email : undefined,
        name:
          (typeof rawUser.name === 'string' && rawUser.name) ||
          (typeof rawUser.full_name === 'string' && rawUser.full_name) ||
          (typeof rawUser.username === 'string' && rawUser.username) ||
          undefined,
      }
    : null;

  return { token, user };
}

export function getLoginEndpoint(): string {
  return `${API_ORIGIN}${LOGIN_PATH.startsWith('/') ? LOGIN_PATH : `/${LOGIN_PATH}`}`;
}

/**
 * Signs in against the tablet-app backend so operators do not have to go to
 * spk2go.mobi. Sends both `email` and `username` because the identifier field
 * may be either, depending on how the account was created.
 */
export async function login({ identifier, password }: Credentials): Promise<Session> {
  const payload = await request<unknown>(getLoginEndpoint(), {
    method: 'POST',
    credentials: LOGIN_WITH_CREDENTIALS ? 'include' : 'same-origin',
    body: {
      email: identifier,
      username: identifier,
      password,
    },
  });

  return toSession(payload);
}

/** Turns an {@link ApiError} into copy a venue owner can act on. */
export function describeLoginError(error: unknown): string {
  if (!(error instanceof ApiError)) {
    return 'Something went wrong while signing you in. Please try again.';
  }

  if (error.isNetworkError) {
    return 'We could not reach the login server. Check your internet connection and try again.';
  }

  if (error.status === 400 || error.status === 401 || error.status === 403) {
    return error.message || 'Those credentials were not recognised. Please check and try again.';
  }

  if (error.isEndpointMissing) {
    return 'The login service is not available at this address yet. Use the SPK2GO portal below while we finish connecting it.';
  }

  if (error.status >= 500) {
    return 'The login server is temporarily unavailable. Please try again in a few minutes.';
  }

  return error.message;
}
